import { FC, useMemo, useState } from 'react';
import { Button, Input, Space, Tabs, Typography } from 'antd';
import { TbBraces } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { DropdownButtonSelect } from '@/shared';
import { PlatformAlert } from '@/shared/components/alerts';
import { AlertDeliveryMethod, AlertMessageTemplateChannel, AlertMessageTemplates } from '@/types';

interface AlertMessageTemplateEditorProps {
    deliveryMethods: AlertDeliveryMethod[];
    onChange: (value: AlertMessageTemplates) => void;
    value: AlertMessageTemplates;
}

type MessageField = 'body' | 'description' | 'title';

const channelLabels: Record<AlertMessageTemplateChannel, string> = {
    email: 'Email',
    inApp: 'In-App',
    push: 'Push',
    slack: 'Slack',
};

const messageFields: Array<{ field: MessageField; label: string; maxLength: number; rows?: number }> = [
    { field: 'title', label: 'Title / Subject', maxLength: 160 },
    { field: 'body', label: 'Body', maxLength: 4000, rows: 3 },
    { field: 'description', label: 'Supporting Description', maxLength: 1000, rows: 2 },
];

const alertMessageTemplateVariables = [
    'alert.name',
    'alert.description',
    'alert.category',
    'workspace.name',
    'match_count',
    'summary',
    'evaluated_at',
    'url',
    'is_test',
] as const;

const AlertMessageTemplateEditor: FC<AlertMessageTemplateEditorProps> = ({ deliveryMethods, onChange, value }) => {
    const { token } = useAntDesignTheme();
    const channels = useMemo(
        () =>
            (['inApp', 'email', 'push', 'slack'] as AlertMessageTemplateChannel[]).filter((channel) =>
                deliveryMethods.includes(channel),
            ),
        [deliveryMethods],
    );
    const [activeChannel, setActiveChannel] = useState<AlertMessageTemplateChannel | undefined>(channels[0]);

    const selectedChannel = activeChannel && channels.includes(activeChannel) ? activeChannel : channels[0];

    if (!selectedChannel || channels.length === 0) {
        return null;
    }

    const updateField = (field: MessageField, fieldValue: string) => {
        const nextChannel = { ...(value[selectedChannel] ?? {}), [field]: fieldValue || undefined };
        const next = { ...value, [selectedChannel]: nextChannel };

        if (!Object.values(nextChannel).some((entry) => entry !== undefined)) {
            delete next[selectedChannel];
        }
        onChange(next);
    };

    const insertVariable = (field: MessageField, variable?: string) => {
        if (!variable) {
            return;
        }
        const current = value[selectedChannel]?.[field] ?? '';
        updateField(field, `${current}${current && !current.endsWith(' ') ? ' ' : ''}{{${variable}}}`);
    };

    return (
        <section
            style={{
                border: `1px dashed ${token.colorBorder}`,
                borderRadius: token.borderRadius,
                marginTop: '0.75rem',
                overflow: 'hidden',
            }}
        >
            <div style={{ padding: '0.75rem 0.75rem 0' }}>
                <Typography.Title level={5} style={{ marginTop: 0 }}>
                    Delivery Message Templates
                </Typography.Title>
                <PlatformAlert
                    status='info'
                    title='Safe dynamic message variables'
                    description='Customize selected notification destinations with the allowlisted Mustache variables below. Templates are stored as plain text and rendered server-side; executable expressions, partials, and unescaped blocks are rejected.'
                    style={{ marginBottom: '0.75rem' }}
                />
            </div>
            <Tabs
                activeKey={selectedChannel}
                items={channels.map((channel) => ({ key: channel, label: channelLabels[channel] }))}
                size='small'
                tabBarStyle={{ marginBottom: 0, padding: '0 0.75rem' }}
                onChange={(channel) => setActiveChannel(channel as AlertMessageTemplateChannel)}
            />
            <Space direction='vertical' size='middle' style={{ padding: '0.75rem', width: '100%' }}>
                {messageFields.map(({ field, label, maxLength, rows }) => (
                    <div key={field}>
                        <div
                            style={{
                                alignItems: 'center',
                                display: 'flex',
                                justifyContent: 'space-between',
                                marginBottom: '0.375rem',
                            }}
                        >
                            <Typography.Text>{label}</Typography.Text>
                            <DropdownButtonSelect<string>
                                matchTriggerWidth={false}
                                menuWidth='18rem'
                                options={alertMessageTemplateVariables.map((variable) => ({
                                    description: `Insert {{${variable}}}`,
                                    icon: <TbBraces />,
                                    label: variable,
                                    value: variable,
                                }))}
                                placeholder='Insert Variable...'
                                onChange={(variable) => insertVariable(field, variable)}
                            >
                                <Button icon={<TbBraces />} size='small'>
                                    Insert Variable
                                </Button>
                            </DropdownButtonSelect>
                        </div>
                        {rows ? (
                            <Input.TextArea
                                autoSize={{ minRows: rows }}
                                maxLength={maxLength}
                                placeholder={`Optional ${label} Override...`}
                                style={{ resize: 'none' }}
                                value={value[selectedChannel]?.[field] ?? ''}
                                onChange={(event) => updateField(field, event.target.value)}
                            />
                        ) : (
                            <Input
                                maxLength={maxLength}
                                placeholder={`Optional ${label} Override...`}
                                value={value[selectedChannel]?.[field] ?? ''}
                                onChange={(event) => updateField(field, event.target.value)}
                            />
                        )}
                    </div>
                ))}
            </Space>
        </section>
    );
};

export default AlertMessageTemplateEditor;
